/** Anything that settles when the upload ends: a Firebase UploadTask is one. */
type UploadLike = { then(done: () => void, failed: () => void): unknown };

/**
 * Task #5: warn before leaving while files are uploading.
 *
 * Every upload is registered here as soon as it starts. While any is running, closing the tab,
 * refreshing or typing a new address shows the browser's "Leave site?" warning. Moving around
 * inside the app is fine: uploads keep running in the background and the banner stays up.
 */

const active = new Set<UploadLike>();
const listeners = new Set<(count: number) => void>();

const WARNING = 'Files are still uploading. If you leave now, those uploads will stop.';

const onBeforeUnload = (event: BeforeUnloadEvent) => {
  if (active.size === 0) return undefined;
  event.preventDefault();
  event.returnValue = WARNING;
  return WARNING;
};

const notify = () => listeners.forEach((fn) => fn(active.size));

export const trackUpload = (task: UploadLike) => {
  if (!task || active.has(task)) return;
  active.add(task);
  if (active.size === 1) window.addEventListener('beforeunload', onBeforeUnload);
  notify();
  const done = () => {
    if (!active.delete(task)) return;
    if (active.size === 0) window.removeEventListener('beforeunload', onBeforeUnload);
    notify();
  };
  // An UploadTask is a promise: it settles when the upload finishes, fails or is cancelled.
  task.then(done, done);
};

export const uploadsInProgress = () => active.size;

export const onUploadsChange = (fn: (count: number) => void) => {
  listeners.add(fn);
  fn(active.size);
  return () => {
    listeners.delete(fn);
  };
};
